const DEFAULT_CATEGORY = {
  objectId: "uncategorized",
  name: "Senza categoria",
  order: 9999,
  color: "#f5f5f5",
  darker_color: "#8c8c8c",
};

export const ANOMALY_META = {
  product_line_non_main_child: {
    label: "Legacy diretto",
    color: "volcano",
    order: 0,
    description:
      "Collegato direttamente al servizio ma non classificato come modulo principale.",
  },
  product_line_inactive_child: {
    label: "Inattivo nel servizio",
    color: "red",
    order: 1,
    description:
      "Collegato direttamente al servizio ma marcato inattivo.",
  },
  included_main_child: {
    label: "Principale incluso",
    color: "magenta",
    order: 2,
    description:
      "Un modulo principale è stato inserito tra gli inclusi automatici.",
  },
  related_main_child: {
    label: "Principale correlato",
    color: "gold",
    order: 3,
    description:
      "Un modulo principale è presente tra i moduli aggiuntivi o correlati.",
  },
};

const ROLE_META = {
  product_line: {
    key: "product_line",
    label: "Servizio",
    color: "geekblue",
  },
  main: {
    key: "main",
    label: "Principale",
    color: "purple",
  },
  addon: {
    key: "addon",
    label: "Aggiuntivo",
    color: "orange",
  },
};

const numberOr = (value, fallback = 0) => {
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : fallback;
};

const dedupeByObjectId = (items = []) => {
  const seen = new Set();

  return items.filter((item) => {
    if (!item?.objectId || seen.has(item.objectId)) {
      return false;
    }

    seen.add(item.objectId);
    return true;
  });
};

const dedupeStrings = (items = []) => Array.from(new Set(items.filter(Boolean)));

export const getModuleCategory = (module) =>
  module?.module_category_pointer || DEFAULT_CATEGORY;

export const getModuleRoleMeta = (module) => {
  if (module?.is_product_line) {
    return ROLE_META.product_line;
  }

  if (module?.is_main) {
    return ROLE_META.main;
  }

  return ROLE_META.addon;
};

export const getModuleRoleLabel = (module) => getModuleRoleMeta(module).label;

export const compareByOrderAndName = (left, right) => {
  const orderDiff = numberOr(left?.order) - numberOr(right?.order);

  if (orderDiff !== 0) {
    return orderDiff;
  }

  return (left?.name || "").localeCompare(right?.name || "", "it");
};

export const compareByCategoryAndOrder = (left, right) => {
  const categoryDiff =
    numberOr(left?.module_category_pointer?.order, DEFAULT_CATEGORY.order) -
    numberOr(right?.module_category_pointer?.order, DEFAULT_CATEGORY.order);

  if (categoryDiff !== 0) {
    return categoryDiff;
  }

  return compareByOrderAndName(left, right);
};

const getRelationNodes = (module, fieldName) =>
  module?.[fieldName]?.edges?.map((edge) => edge?.node).filter(Boolean) || [];

const getRelationIds = (module, fieldName) =>
  getRelationNodes(module, fieldName).map((node) => node.objectId).filter(Boolean);

const pushUniqueMapItem = (map, key, value) => {
  if (!key || !value?.objectId) {
    return;
  }

  const current = map.get(key) || [];

  if (current.some((item) => item.objectId === value.objectId)) {
    return;
  }

  current.push(value);
  map.set(key, current);
};

const getMapArray = (map, key) => map.get(key) || [];

const getUniqueModules = (items = []) => dedupeByObjectId(items).sort(compareByOrderAndName);

const buildCategoryGroups = (modules = []) => {
  const groups = new Map();

  modules.forEach((module) => {
    const category = getModuleCategory(module);
    const key = category.objectId || DEFAULT_CATEGORY.objectId;

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        category,
        modules: [],
      });
    }

    groups.get(key).modules.push(module);
  });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      modules: group.modules.sort(compareByCategoryAndOrder),
    }))
    .sort(
      (left, right) =>
        numberOr(left.category?.order, DEFAULT_CATEGORY.order) -
        numberOr(right.category?.order, DEFAULT_CATEGORY.order)
    );
};

const getAnomalyMeta = (type) => ANOMALY_META[type] || ANOMALY_META.related_main_child;

export const buildModuleCatalogData = (modules = []) => {
  const moduleMap = new Map(
    modules.map((module) => [
      module.objectId,
      {
        ...module,
        roleMeta: getModuleRoleMeta(module),
        roleLabel: getModuleRoleLabel(module),
      },
    ])
  );
  const productLines = modules
    .filter((module) => module.is_product_line)
    .map((module) => moduleMap.get(module.objectId))
    .sort(compareByOrderAndName);
  const mainModules = modules
    .filter((module) => !module.is_product_line && module.is_main)
    .map((module) => moduleMap.get(module.objectId))
    .sort(compareByCategoryAndOrder);
  const productLineParentsByChildId = new Map();
  const includedParentsByChildId = new Map();
  const relatedParentsByChildId = new Map();
  const anomalyRows = [];
  const anomalyKeys = new Set();
  const anomalyRowsByModuleId = new Map();

  const registerAnomaly = ({
    type,
    relationType,
    parent,
    child,
    serviceParents = [],
  }) => {
    if (!parent?.objectId || !child?.objectId) {
      return;
    }

    const key = [type, relationType, parent.objectId, child.objectId].join(":");

    if (anomalyKeys.has(key)) {
      return;
    }

    anomalyKeys.add(key);

    const meta = getAnomalyMeta(type);
    const row = {
      key,
      type,
      relationType,
      parent,
      child,
      label: meta.label,
      color: meta.color,
      description: meta.description,
      sortOrder: meta.order,
      serviceParents,
      serviceParentIds: serviceParents.map((service) => service.objectId),
      serviceParentNames: serviceParents.map((service) => service.name),
    };

    anomalyRows.push(row);

    [parent, child].forEach((module) => {
      const current = anomalyRowsByModuleId.get(module.objectId) || [];
      current.push(row);
      anomalyRowsByModuleId.set(module.objectId, current);
    });
  };

  productLines.forEach((productLine) => {
    const directChildren = getRelationIds(productLine, "related_service_relation")
      .map((childId) => moduleMap.get(childId))
      .filter(Boolean);

    directChildren.forEach((child) => {
      pushUniqueMapItem(productLineParentsByChildId, child.objectId, productLine);

      if (!child.is_active) {
        registerAnomaly({
          type: "product_line_inactive_child",
          relationType: "product_line",
          parent: productLine,
          child,
          serviceParents: [productLine],
        });
      } else if (!child.is_product_line && !child.is_main) {
        registerAnomaly({
          type: "product_line_non_main_child",
          relationType: "product_line",
          parent: productLine,
          child,
          serviceParents: [productLine],
        });
      }
    });
  });

  mainModules.forEach((mainModule) => {
    const parentServiceParents = getMapArray(
      productLineParentsByChildId,
      mainModule.objectId
    );

    getRelationIds(mainModule, "included_service_relation")
      .map((childId) => moduleMap.get(childId))
      .filter(Boolean)
      .forEach((child) => {
        pushUniqueMapItem(includedParentsByChildId, child.objectId, mainModule);

        if (child.is_main) {
          registerAnomaly({
            type: "included_main_child",
            relationType: "included",
            parent: mainModule,
            child,
            serviceParents: parentServiceParents,
          });
        }
      });

    getRelationIds(mainModule, "related_service_relation")
      .map((childId) => moduleMap.get(childId))
      .filter(Boolean)
      .forEach((child) => {
        pushUniqueMapItem(relatedParentsByChildId, child.objectId, mainModule);

        if (child.is_main) {
          registerAnomaly({
            type: "related_main_child",
            relationType: "related",
            parent: mainModule,
            child,
            serviceParents: parentServiceParents,
          });
        }
      });
  });

  const getServiceParentsForModule = (moduleId) => {
    const directParents = getMapArray(productLineParentsByChildId, moduleId);
    const mainParents = dedupeByObjectId([
      ...getMapArray(includedParentsByChildId, moduleId),
      ...getMapArray(relatedParentsByChildId, moduleId),
    ]);
    const derivedParents = mainParents.flatMap((parent) =>
      getMapArray(productLineParentsByChildId, parent.objectId)
    );

    return getUniqueModules([...directParents, ...derivedParents]);
  };

  const getIncludedParentsForModule = (moduleId) =>
    getUniqueModules(getMapArray(includedParentsByChildId, moduleId));

  const getRelatedParentsForModule = (moduleId) =>
    getUniqueModules(getMapArray(relatedParentsByChildId, moduleId));

  const getMainParentsForModule = (moduleId) =>
    getUniqueModules([
      ...getIncludedParentsForModule(moduleId),
      ...getRelatedParentsForModule(moduleId),
    ]);

  const enrichRelatedModule = (module) => {
    const serviceParents = getServiceParentsForModule(module.objectId);
    const includedParents = getIncludedParentsForModule(module.objectId);
    const relatedParents = getRelatedParentsForModule(module.objectId);
    const mainParents = getMainParentsForModule(module.objectId);
    const anomalyEntries = anomalyRowsByModuleId.get(module.objectId) || [];

    return {
      ...module,
      serviceParents,
      serviceParentNames: serviceParents.map((parent) => parent.name),
      serviceParentIds: serviceParents.map((parent) => parent.objectId),
      mainParents,
      mainParentNames: mainParents.map((parent) => parent.name),
      includedByParents: includedParents,
      includedByNames: includedParents.map((parent) => parent.name),
      includedByCount: includedParents.length,
      relatedByParents: relatedParents,
      relatedByNames: relatedParents.map((parent) => parent.name),
      relatedByCount: relatedParents.length,
      sharedByCount: includedParents.length,
      sharedByNames: includedParents.map((parent) => parent.name),
      anomalyEntries,
      anomalyFlags: dedupeStrings(anomalyEntries.map((entry) => entry.type)),
      hasAnomaly: anomalyEntries.length > 0,
    };
  };

  const moduleDetailsById = new Map(
    mainModules.map((module) => {
      const includedChildren = getRelationIds(module, "included_service_relation")
        .map((childId) => moduleMap.get(childId))
        .filter(Boolean)
        .map(enrichRelatedModule)
        .sort(compareByCategoryAndOrder);
      const relatedChildren = getRelationIds(module, "related_service_relation")
        .map((childId) => moduleMap.get(childId))
        .filter(Boolean)
        .map(enrichRelatedModule)
        .sort(compareByCategoryAndOrder);
      const anomalyEntries = anomalyRowsByModuleId.get(module.objectId) || [];

      return [
        module.objectId,
        {
          includedChildren,
          relatedChildren,
          includedCount: includedChildren.length,
          relatedCount: relatedChildren.length,
          anomalyEntries,
          anomalyFlags: dedupeStrings(anomalyEntries.map((entry) => entry.type)),
          hasAnomaly: anomalyEntries.length > 0,
        },
      ];
    })
  );

  const enrichedModules = modules
    .map((module) => {
      const normalized = moduleMap.get(module.objectId) || module;
      const serviceParents = getServiceParentsForModule(module.objectId);
      const includedParents = getIncludedParentsForModule(module.objectId);
      const relatedParents = getRelatedParentsForModule(module.objectId);
      const mainParents = getMainParentsForModule(module.objectId);
      const anomalyEntries = anomalyRowsByModuleId.get(module.objectId) || [];
      const details = moduleDetailsById.get(module.objectId);

      return {
        ...normalized,
        serviceParents,
        serviceParentNames: serviceParents.map((parent) => parent.name),
        serviceParentIds: serviceParents.map((parent) => parent.objectId),
        mainParents,
        mainParentNames: mainParents.map((parent) => parent.name),
        includedByParents: includedParents,
        includedByNames: includedParents.map((parent) => parent.name),
        includedByCount: includedParents.length,
        relatedByParents: relatedParents,
        relatedByNames: relatedParents.map((parent) => parent.name),
        relatedByCount: relatedParents.length,
        directProductLineParents: getUniqueModules(
          getMapArray(productLineParentsByChildId, module.objectId)
        ),
        directProductLineParentNames: getMapArray(
          productLineParentsByChildId,
          module.objectId
        ).map((parent) => parent.name),
        sharedByCount: includedParents.length,
        sharedByNames: includedParents.map((parent) => parent.name),
        includedCount:
          details?.includedCount || getRelationIds(module, "included_service_relation").length,
        relatedCount:
          details?.relatedCount || getRelationIds(module, "related_service_relation").length,
        anomalyEntries,
        anomalyFlags: dedupeStrings(anomalyEntries.map((entry) => entry.type)),
        hasAnomaly: anomalyEntries.length > 0,
      };
    })
    .sort(compareByCategoryAndOrder);

  const productLineSections = productLines.map((productLine) => {
    const directChildren = getRelationIds(productLine, "related_service_relation")
      .map((childId) => moduleMap.get(childId))
      .filter(Boolean)
      .map(enrichRelatedModule)
      .sort(compareByCategoryAndOrder);
    const mainChildren = directChildren
      .filter((child) => !child.is_product_line && child.is_main)
      .map((child) => ({
        ...child,
        ...moduleDetailsById.get(child.objectId),
      }));
    const activeMainChildren = mainChildren.filter((child) => child.is_active);
    const inactiveMainChildren = mainChildren.filter((child) => !child.is_active);
    const legacyActiveChildren = directChildren.filter(
      (child) => child.is_active && !child.is_product_line && !child.is_main
    );
    const inactiveChildren = directChildren.filter(
      (child) => !child.is_active && !child.is_product_line && !child.is_main
    );
    const mainGroups = buildCategoryGroups(mainChildren);
    const anomalyEntries = anomalyRows.filter(
      (row) =>
        row.parent.objectId === productLine.objectId ||
        row.serviceParentIds.includes(productLine.objectId)
    );

    return {
      productLine: enrichRelatedModule(productLine),
      directChildren,
      mainChildren,
      activeMainChildren,
      inactiveMainChildren,
      legacyActiveChildren,
      inactiveChildren,
      mainGroups,
      mainCount: mainChildren.length,
      activeMainCount: activeMainChildren.length,
      legacyCount: legacyActiveChildren.length,
      inactiveCount: inactiveChildren.length,
      oneTimeCount: mainChildren.filter((child) => child.una_tantum).length,
      anomalyEntries,
      hasAnomaly: anomalyEntries.length > 0,
    };
  });

  return {
    modules: enrichedModules,
    anomalyRows: anomalyRows.sort(
      (left, right) =>
        left.sortOrder - right.sortOrder ||
        compareByOrderAndName(left.parent, right.parent) ||
        compareByOrderAndName(left.child, right.child)
    ),
    productLineSections,
    stats: {
      total: modules.length,
      active: modules.filter((module) => module.is_active).length,
      inactive: modules.filter((module) => !module.is_active).length,
      productLines: modules.filter((module) => module.is_product_line).length,
      mainModules: modules.filter(
        (module) => !module.is_product_line && module.is_main
      ).length,
      addonModules: modules.filter(
        (module) => !module.is_product_line && !module.is_main
      ).length,
      oneTimeModules: modules.filter((module) => module.una_tantum).length,
      anomalies: anomalyRows.length,
    },
  };
};
